import {
  createUser,
  deleteUser,
  fetchUsers,
  updateUser,
  useAppDispatch,
  useAppSelector,
} from "@template/state";
import type { Role, User } from "@template/types";
import { Button, Input, Modal, showToast, Tabs } from "@template/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useFinance } from "./FinanceContext";
import { seedLogins } from "./data/seedData";
import { ROLES } from "./permissions";
import { getErrorMessage } from "../../lib/error-message";

export function UsersAccessPage() {
  const dispatch = useAppDispatch();
  const users = useAppSelector((s) => s.users.users);
  const status = useAppSelector((s) => s.users.status);
  const { has, audit, userEmail } = useFinance();
  const [tab, setTab] = useState<"users" | "logins">("users");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<Partial<User> | null>(null);
  const [histFor, setHistFor] = useState<User | null>(null);

  const canAll = has("users.manage");
  const canFT = has("users.manageFinanceTeam");
  const canManage = (u: User) => canAll || (canFT && u.role === "Finance Team");
  const canAdd = canAll || canFT;

  useEffect(() => {
    dispatch(fetchUsers()).catch(() => {
      /* surfaced via status === "failed" below */
    });
  }, [dispatch]);

  async function toggleActive(u: User) {
    try {
      await dispatch(updateUser({ id: u.id, payload: { isActive: !u.isActive } })).unwrap();
      showToast(u.isActive ? "User disabled" : "User enabled", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Could not update user"), "error");
    }
  }

  async function remove(u: User) {
    if (!window.confirm(`Permanently delete ${u.displayName || u.email}? This cannot be undone.`)) {
      return;
    }
    try {
      await dispatch(deleteUser(u.id)).unwrap();
      showToast("User deleted", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Could not delete user"), "error");
    }
  }

  async function save(form: Partial<User> & { email: string; role: Role }) {
    try {
      if (form.id) {
        await dispatch(
          updateUser({
            id: form.id,
            payload: { role: form.role, displayName: form.displayName ?? undefined },
          }),
        ).unwrap();
        showToast("User updated", "success");
      } else {
        await dispatch(
          createUser({
            email: form.email,
            role: form.role,
            displayName: form.displayName ?? undefined,
          }),
        ).unwrap();
        showToast("User created", "success");
      }
      setEdit(null);
    } catch (err) {
      showToast(getErrorMessage(err, "Could not save user"), "error");
    }
  }

  const query = q.trim().toLowerCase();
  const rows = users.filter(
    (u) =>
      !query ||
      u.email.toLowerCase().includes(query) ||
      (u.displayName ?? "").toLowerCase().includes(query) ||
      u.role.toLowerCase().includes(query),
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Users &amp; Access
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Administration · user lifecycle · role assignment · login history{" "}
        {canAll ? "" : canFT ? "(you can manage Finance Team users)" : "(view only)"}
      </Typography>

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as "users" | "logins")}
        items={[
          { value: "users", label: "Users" },
          { value: "logins", label: "Login History" },
        ]}
      />

      <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
        {tab === "users" ? (
          <TextField
            size="small"
            label="Search name / email / role…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        ) : null}
        {tab === "users" && canAdd ? (
          <Button
            variant="contained"
            onClick={() =>
              setEdit({
                email: "",
                role: canAll ? "Finance Team" : "Finance Team",
                displayName: "",
              })
            }
          >
            + Add User
          </Button>
        ) : null}
      </Stack>

      {status === "failed" ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load users from the server.
        </Alert>
      ) : null}

      {tab === "users" ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((u) => {
                const m = canManage(u);
                return (
                  <TableRow key={u.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{u.displayName || "—"}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={u.isActive ? "Active" : "Disabled"}
                        color={u.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {m ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button size="small" onClick={() => setEdit(u)}>
                            Edit
                          </Button>
                          <Button size="small" onClick={() => toggleActive(u)}>
                            {u.isActive ? "Disable" : "Enable"}
                          </Button>
                          <Button size="small" onClick={() => setHistFor(u)}>
                            History
                          </Button>
                          {u.email !== userEmail ? (
                            <Button size="small" color="error" onClick={() => remove(u)}>
                              Delete
                            </Button>
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No access
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    {status === "loading" ? "Loading users…" : "No users found."}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seedLogins.map((l) => (
                <TableRow key={l.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{l.name}</TableCell>
                  <TableCell>{l.email}</TableCell>
                  <TableCell>{l.role}</TableCell>
                  <TableCell>{l.time}</TableCell>
                  <TableCell>{l.ip}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={l.result}
                      color={/Success/.test(l.result) ? "success" : "error"}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {seedLogins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No login history recorded yet.</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          <Alert severity="info" sx={{ mt: 2 }}>
            Login history is not yet recorded server-side (no backend login-audit endpoint yet).
          </Alert>
        </TableContainer>
      )}

      {edit ? (
        <UserModal user={edit} canAll={canAll} onSave={save} onClose={() => setEdit(null)} />
      ) : null}

      {histFor ? (
        <Modal
          open
          onClose={() => setHistFor(null)}
          title={`Activity — ${histFor.displayName || histFor.email}`}
          maxWidth="sm"
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {histFor.role} · {histFor.email}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Login History
          </Typography>
          <TableContainer sx={{ maxHeight: 180, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {seedLogins
                  .filter((l) => l.email === histFor.email)
                  .map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.time}</TableCell>
                      <TableCell>{l.ip}</TableCell>
                      <TableCell>{l.result}</TableCell>
                    </TableRow>
                  ))}
                {!seedLogins.some((l) => l.email === histFor.email) ? (
                  <TableRow>
                    <TableCell colSpan={3}>No login records.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Audit Actions
          </Typography>
          <TableContainer sx={{ maxHeight: 180 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audit
                  .filter(
                    (a) => a.detail && a.detail.indexOf(histFor.displayName || histFor.email) >= 0,
                  )
                  .slice(0, 10)
                  .map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.action}</TableCell>
                      <TableCell>{a.by}</TableCell>
                      <TableCell>{a.date}</TableCell>
                      <TableCell>{a.detail}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Modal>
      ) : null}
    </Box>
  );
}

function UserModal({
  user,
  canAll,
  onSave,
  onClose,
}: {
  user: Partial<User>;
  canAll: boolean;
  onSave: (form: Partial<User> & { email: string; role: Role }) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(user.email ?? "");
  const [displayName, setDisplayName] = useState(user.displayName ?? "");
  const [role, setRole] = useState<Role>(user.role ?? "Finance Team");
  const roleOpts: Role[] = canAll ? ROLES : ["Finance Team"];
  const valid = Boolean(displayName.trim()) && /@haystackrobotics\.com$/i.test(email.trim());

  return (
    <Modal
      open
      onClose={onClose}
      title={user.id ? "Edit User" : "Add User"}
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!valid}
            onClick={() =>
              onSave({ id: user.id, email: email.trim(), role, displayName: displayName.trim() })
            }
          >
            {user.id ? "Save changes" : "Create user"}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Input
          label="Employee Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <Input
          label="Corporate Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@haystackrobotics.com"
          disabled={Boolean(user.id)}
        />
        <TextField
          select
          label="Role"
          size="medium"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
        >
          {roleOpts.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        {!valid ? (
          <Alert severity="warning">
            Name and a valid @haystackrobotics.com email are required.
          </Alert>
        ) : null}
      </Stack>
    </Modal>
  );
}
